<template>
    <QuestionBox 
        :title="step.question" 
        :text="correctAnswers.length === 1 
            ? $t('module.multipleChoiceTextSingle') 
            : $t('module.multipleChoiceText')" 
    />
    <ul class="m-customPicker u-listUnstyled" :class="{ '-selected': selectedAnswers ? selectedAnswers.length > 0 : false }">
        <li v-for="(answer, index) in step.answers" :key="index">
            <label>
                <input 
                    :type="correctAnswers?.length === 1 ? 'radio' : 'checkbox'" 
                    :value="answer.answer" 
                    v-model="selectedAnswers" 
                    :disabled="isChecking || (correctAnswers?.length > 1 && selectedAnswers.length >= correctAnswers.length && !selectedAnswers.includes(answer.answer))"
                    @click="handleSelection(answer.answer)"
                    :class="{
                        '-correct': isChecking && (Array.isArray(selectedAnswers)
                            ? selectedAnswers.includes(answer.answer) && correctAnswers.includes(answer.answer)
                            : selectedAnswers === answer.answer && correctAnswers.includes(answer.answer)),
                        '-incorrect': isChecking && (Array.isArray(selectedAnswers)
                            ? selectedAnswers.includes(answer.answer) && !correctAnswers.includes(answer.answer)
                            : selectedAnswers === answer.answer && !correctAnswers.includes(answer.answer))
                    }"
                />
                <div class="m-customPicker__item u-borderPrimary u-mb10">
                    {{ answer.answer }}
                </div>
            </label>
        </li>
    </ul>
    <FixedButton 
        :isDisabled="!selectedAnswers || selectedAnswers.length === 0" 
        @click="handleButtonClick" 
        :buttonClass="showTryAgain ? '-reset' : ''">
        {{ showTryAgain ? $t('module.tryAgain') : (answeredCorrect ? $t('module.nextQuestion') : $t('module.checkQuestion')) }}
    </FixedButton>
</template>

<script setup>
import { inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { addAttempt } from '../../utils/globalFunctions';

import QuestionBox from './QuestionBox.vue';
import FixedButton from './FixedButton.vue';

const props = defineProps({
    step: {
        type: Object,
    },
});

const { t } = useI18n(); 

const correctAnswers = (props.step.answers || []).filter(answer => answer.correct).map(answer => answer.answer) || [];

const selectedAnswers = ref(correctAnswers.length === 1 ? null : []);
const showTryAgain = ref(false);
const answeredCorrect = ref(false);
const isChecking = ref(false);

const completeStep = inject('completeStep');
const showNotice = inject('showNotice');
const hideNotice = inject('hideNotice');

const attempts = defineModel('attempts');

const resetQuestion = () => {
    selectedAnswers.value = correctAnswers.length === 1 ? null : [];
    showTryAgain.value = false;
    isChecking.value = false;
    hideNotice();
};

const checkAnswer = () => {
    if (!selectedAnswers.value || (Array.isArray(selectedAnswers.value) && selectedAnswers.value.length === 0)) {
        return;
    }

    isChecking.value = true;
    let correctCount = 0;
    let selectedCount = 0;

    if (Array.isArray(selectedAnswers.value)) {
        correctCount = selectedAnswers.value.filter(answer => correctAnswers.includes(answer)).length;
        selectedCount = selectedAnswers.value.length;
    } else {
        correctCount = correctAnswers.includes(selectedAnswers.value) ? 1 : 0;
        selectedCount = 1;
    }

    if (correctCount === selectedCount && correctCount === correctAnswers.length) {
        // All correct
        attempts.value.push(addAttempt(props.step.uuid, true));
        answeredCorrect.value = true;
        completeStep(props.step.uuid);
        const textCorrect = props.step.feedback_correct ? props.step.feedback_correct : t('module.correctAnswerText');
        showNotice(t('module.correctAnswerTitle'), textCorrect, '-success');
    } else if (correctCount === 0) {
        // All incorrect
        attempts.value.push(addAttempt(props.step.uuid, false));
        showNotice(
            t('module.wrongAnswerTitle'), 
            correctCount === 0 
                ? t('module.wrongAnswerText') 
                : t('module.wrongAnswerTextMultiple', { n: correctAnswers.length }), 
            '-error'
        );
        showTryAgain.value = true;
    } else {
        // Partially correct
        attempts.value.push(addAttempt(props.step.uuid, false));
        showNotice(
            t('module.partiallyWrongAnswerTitle'), 
            t('module.partiallyWrongAnswerText', { 
                selected: correctCount,
                total: correctAnswers.length 
            }), 
            '-error'
        );
        showTryAgain.value = true;
    }
};

const handleButtonClick = () => {
    if (showTryAgain.value) {
        resetQuestion();
    } else if (answeredCorrect.value) {
        completeStep(props.step.uuid, true, false);
        hideNotice();
    } else {
        checkAnswer();
    }
};

const handleSelection = (answer) => {
    if (correctAnswers.length === 1) {
        selectedAnswers.value = answer; // For radio, set the single selected value
    } else if (selectedAnswers.value.includes(answer)) {
        selectedAnswers.value = selectedAnswers.value.filter(ans => ans !== answer);
    } else if (selectedAnswers.value.length < correctAnswers.length) {
        selectedAnswers.value.push(answer);
    }
};
</script>
